import React, { useState } from 'react';
import { DesignerNode, MAX_SIGNATURE_PARTIES, SignatureParty, uid } from './model';

/** Standardmäßig stehen zwei Unterschriften nebeneinander. */
export const DEFAULT_SIGNATURES_PER_ROW = 2;
/** Freier Raum über der Unterschriftslinie in Punkt. */
export const DEFAULT_SIGN_HEIGHT = 40;

export const newSignatureParty = (name = ''): SignatureParty => ({ id: uid(), name });

/** Unterschriftenblock aus den zuvor eingegebenen Namen der Vertragsparteien. */
export function newSignatureBlock(names: string[]): DesignerNode {
  return {
    id: uid(),
    type: 'signatureBlock',
    perRow: DEFAULT_SIGNATURES_PER_ROW,
    signHeight: DEFAULT_SIGN_HEIGHT,
    parties: names.map(name => newSignatureParty(name.trim())),
    style: { marginTop: 24 },
  };
}

/** Teilt die Vertragsparteien in Zeilen zu je `perRow` Unterschriften auf. */
export function signatureRows(parties: SignatureParty[], perRow: number): SignatureParty[][] {
  const rows: SignatureParty[][] = [];
  for (let index = 0; index < parties.length; index += perRow) rows.push(parties.slice(index, index + perRow));
  return rows;
}

export const clampPerRow = (value?: number) => Math.max(1, Math.min(4, value || DEFAULT_SIGNATURES_PER_ROW));

const ordinal = (index: number) => `${index + 1}.`;

/**
 * Zweistufiger Dialog: zuerst die Anzahl der Vertragsparteien bestätigen,
 * danach je ein Textfeld für den Namen jeder Partei ausfüllen.
 */
export default function SignatureBlockDialog({ onInsert, onClose }: { onInsert: (node: DesignerNode) => void; onClose: () => void }) {
  const [count, setCount] = useState(2);
  const [names, setNames] = useState<string[] | null>(null);

  const confirmCount = () => {
    const total = Math.max(1, Math.min(MAX_SIGNATURE_PARTIES, Math.round(count) || 1));
    setCount(total);
    setNames(current => Array.from({ length: total }, (_unused, index) => current?.[index] ?? ''));
  };

  const complete = !!names?.length && names.every(name => name.trim());

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Unterschriftenblock einfügen">
      <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-lg max-h-[85vh] overflow-auto">
        <h3 className="font-semibold mb-1">Unterschriftenblock einfügen</h3>
        <p className="text-xs text-gray-500 mb-4">
          Der Block erzeugt im Vertrag eine Tabelle mit je zwei Unterschriften pro Zeile: oben die Unterschriftslinie,
          darunter mittig der Name der Vertragspartei.
        </p>

        {names === null ? (
          <>
            <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="signature-count">Anzahl der Vertragsparteien</label>
            <input
              id="signature-count"
              autoFocus
              type="number"
              min={1}
              max={MAX_SIGNATURE_PARTIES}
              className="w-full border border-gray-300 rounded p-2 text-sm"
              value={count}
              onChange={event => setCount(Number(event.target.value))}
              onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); confirmCount(); } }}
            />
            <p className="mt-1 text-[11px] text-gray-400">Höchstens {MAX_SIGNATURE_PARTIES} Vertragsparteien.</p>
            <div className="flex justify-end gap-2 mt-5">
              <button type="button" onClick={onClose} className="border rounded px-4 py-2 text-sm">Abbrechen</button>
              <button type="button" onClick={confirmCount} className="bg-primary-600 text-white rounded px-4 py-2 text-sm">Bestätigen</button>
            </div>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-600 mb-2">Namen der {names.length} Vertragsparteien eingeben:</p>
            <div className="space-y-2">
              {names.map((name, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="w-6 text-xs text-gray-400 text-right shrink-0">{ordinal(index)}</span>
                  <input
                    autoFocus={index === 0}
                    aria-label={`Name der ${ordinal(index)} Vertragspartei`}
                    placeholder="Name der Vertragspartei"
                    className="flex-1 border border-gray-300 rounded p-2 text-sm"
                    value={name}
                    onChange={event => setNames(current => (current || []).map((entry, position) => (position === index ? event.target.value : entry)))}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between gap-2 mt-5">
              <button type="button" onClick={() => setNames(null)} className="border rounded px-4 py-2 text-sm">Zurück</button>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="border rounded px-4 py-2 text-sm">Abbrechen</button>
                <button
                  type="button"
                  disabled={!complete}
                  onClick={() => onInsert(newSignatureBlock(names))}
                  className="bg-primary-600 text-white rounded px-4 py-2 text-sm disabled:opacity-40"
                >
                  Einfügen
                </button>
              </div>
            </div>
            {!complete && <p className="mt-2 text-[11px] text-amber-600">Bitte für jede Vertragspartei einen Namen eingeben.</p>}
          </>
        )}
      </div>
    </div>
  );
}
